/**
 * CCCHAPS Academy Portal — Admin "Preview as Learner" client-side state.
 *
 * Stores preview progress + quiz attempts in sessionStorage so refresh works
 * but tab-close ends the preview. Nothing here is persisted server-side; the
 * only server calls made during preview are:
 *   - GET  /api/admin/portal/preview/course        (admin-only)
 *   - POST /api/admin/portal/preview/quiz-evaluate (admin-only, no persistence)
 *   - GET  /api/admin/portal/preview/certificate.pdf (admin-only, sample only)
 *
 * A regular learner cannot activate preview: even if they toggle the flag in
 * DevTools, all preview API calls require an admin session cookie.
 */

const STATE_KEY = "ccchaps_preview_state";
const FLAG_KEY = "ccchaps_preview_active";
const CODE_KEY = "ccchaps_preview_course_code";

const emptyState = () => ({
  active: true,
  started_at: new Date().toISOString(),
  progress: {
    section_completions: {},           // {sectionId: iso}
    case_study_completed: false,
    case_study_responses: {},
    evaluation_submitted: false,
    current_section: "welcome",
  },
  quiz_attempts: [],                    // [{attempt_number, score, total, passed, review, submitted_at}]
  latest_quiz: null,
  best_quiz: null,
  evaluation: null,
  certificate: null,                    // sample cert object (client-side only)
});

const SAMPLE_CERT = {
  id: "preview-sample",
  cert_number: "PREVIEW-CTA-001",
  participant_full_name: "ADMIN PREVIEW",
  knowledge_check_score: 10,
  knowledge_check_total: 10,
  sample: true,
};

export const previewStore = {
  isActive() {
    try {
      return sessionStorage.getItem(FLAG_KEY) === "1";
    } catch { return false; }
  },
  enter(courseCode) {
    try {
      sessionStorage.setItem(FLAG_KEY, "1");
      sessionStorage.setItem(CODE_KEY, (courseCode || "CTA-001").toUpperCase());
      if (!sessionStorage.getItem(STATE_KEY)) {
        sessionStorage.setItem(STATE_KEY, JSON.stringify(emptyState()));
      }
    } catch { /* storage unavailable — preview simply won't persist across nav */ }
  },
  getCourseCode() {
    try { return sessionStorage.getItem(CODE_KEY) || "CTA-001"; } catch { return "CTA-001"; }
  },
  exit() {
    try {
      sessionStorage.removeItem(FLAG_KEY);
      sessionStorage.removeItem(STATE_KEY);
      sessionStorage.removeItem(CODE_KEY);
    } catch { void 0; }
  },
  reset() {
    try {
      sessionStorage.setItem(STATE_KEY, JSON.stringify(emptyState()));
    } catch { void 0; }
  },
  getState() {
    try {
      const raw = sessionStorage.getItem(STATE_KEY);
      if (!raw) return emptyState();
      const parsed = JSON.parse(raw);
      // Defensive hydration — if a stale sessionStorage snapshot is missing
      // any top-level slice (e.g., it was written before section_completions
      // existed), merge in the default shape so downstream reads never
      // crash with "Cannot read properties of undefined". This is the fix
      // for the uncaught TypeError in markSectionComplete observed during
      // the FD2G admin-preview walkthrough.
      const defaults = emptyState();
      return {
        ...defaults,
        ...parsed,
        progress: { ...defaults.progress, ...(parsed.progress || {}) },
        quizAttempts: { ...defaults.quizAttempts, ...(parsed.quizAttempts || {}) },
      };
    } catch { return emptyState(); }
  },
  _save(s) {
    try { sessionStorage.setItem(STATE_KEY, JSON.stringify(s)); } catch { void 0; }
  },
  markSectionComplete(sectionId) {
    const s = this.getState();
    s.progress.section_completions[sectionId] = new Date().toISOString();
    s.progress.current_section = sectionId;
    this._save(s);
    return s;
  },
  markAllComplete(sectionIds) {
    const s = this.getState();
    const now = new Date().toISOString();
    sectionIds.forEach(id => { s.progress.section_completions[id] = now; });
    this._save(s);
    return s;
  },
  submitCaseStudy(responses) {
    const s = this.getState();
    s.progress.case_study_completed = true;
    s.progress.case_study_responses = responses || {};
    this._save(s);
    return s;
  },
  recordQuizAttempt(result) {
    const s = this.getState();
    const attempt = {
      attempt_number: s.quiz_attempts.length + 1,
      score: result.score,
      total: result.total,
      passed: result.passed,
      review: result.review,
      submitted_at: new Date().toISOString(),
    };
    s.quiz_attempts.push(attempt);
    s.latest_quiz = attempt;
    s.best_quiz = s.quiz_attempts.reduce((best, a) => (!best || a.score > best.score ? a : best), null);
    this._save(s);
    return { ...attempt, review: result.review };
  },
  submitEvaluation(evalPayload) {
    const s = this.getState();
    if (!s.best_quiz || !s.best_quiz.passed) {
      return { ok: false, error: "Please pass the knowledge check before submitting your evaluation." };
    }
    if (s.evaluation) {
      return { ok: false, error: "Evaluation already submitted in this preview session." };
    }
    s.evaluation = {
      ...evalPayload,
      submitted_at: new Date().toISOString(),
    };
    s.progress.evaluation_submitted = true;
    s.certificate = {
      ...SAMPLE_CERT,
      completion_date: new Date().toISOString(),
    };
    this._save(s);
    return { ok: true, certificate: s.certificate };
  },
  getSampleCert() {
    const s = this.getState();
    return s.certificate;
  },
  /** PVC-001 (and future courses): persist free-text reflection entries by inputId. */
  setReflection(inputId, text) {
    if (!inputId) return;
    const s = this.getState();
    s.progress.case_study_responses = { ...(s.progress.case_study_responses || {}), [inputId]: text || "" };
    this._save(s);
  },
  getReflection(inputId) {
    if (!inputId) return "";
    const s = this.getState();
    return (s.progress.case_study_responses || {})[inputId] || "";
  },
};

/** Build the same {course, enrollment, progress, latest_quiz, best_quiz, evaluation, certificate}
 *  bundle that the learner-side GET /portal/courses/CTA-001 returns, but sourced entirely
 *  from client-side preview state. */
export function buildPreviewCourseBundle(course) {
  const s = previewStore.getState();
  const isCompleted = !!s.evaluation;
  return {
    course,
    enrollment: {
      id: "preview-enrollment",
      learner_id: "preview-admin",
      course_id: course.id,
      course_code: course.code,
      enrolled_at: s.started_at,
      status: isCompleted ? "completed" : "in_progress",
      completed_at: isCompleted ? s.evaluation.submitted_at : null,
      certificate_id: s.certificate ? s.certificate.id : null,
    },
    progress: {
      id: "preview-progress",
      enrollment_id: "preview-enrollment",
      section_completions: s.progress.section_completions,
      case_study_completed: s.progress.case_study_completed,
      case_study_responses: s.progress.case_study_responses,
      evaluation_submitted: s.progress.evaluation_submitted,
      current_section: s.progress.current_section,
    },
    latest_quiz: s.latest_quiz,
    best_quiz: s.best_quiz,
    evaluation: s.evaluation,
    certificate: s.certificate,
    preview: true,
  };
}

export const PREVIEW_LEARNER = {
  id: "preview-admin",
  email: "preview@ccchaps.local",
  first_name: "Admin",
  last_name: "Preview",
  organization: "CCCHAPS Preview",
  chaplain_role: null,
};
